import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";

const API_URL = process.env.REACT_APP_API_URL;

export default function StripeCallback() {
  const auth = useAuth();
  const [status, setStatus] = useState("checking"); // "checking" | "success" | "incomplete" | "error"

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const params = new URLSearchParams(window.location.search);
    const companyId = params.get("company_id");
    const isRefresh = params.get("refresh") === "true";

    if (!companyId) {
      setStatus("error");
      return;
    }

    // If Stripe sent us back for a refresh, re-kick the onboarding link
    if (isRefresh) {
      const resume = async () => {
        try {
          const res = await fetch(`${API_URL}/stripe/connect/onboard`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${auth.user?.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ company_id: companyId, origin: window.location.origin }),
          });
          if (!res.ok) throw new Error("Could not resume onboarding");
          const { url } = await res.json();
          window.location.href = url;
        } catch {
          setStatus("error");
        }
      };
      resume();
      return;
    }

    // Normal return — check if onboarding completed
    const checkStatus = async () => {
      try {
        const res = await fetch(
          `${API_URL}/stripe/connect/status/${companyId}`,
          { headers: { Authorization: `Bearer ${auth.user?.access_token}` } }
        );
        if (!res.ok) throw new Error("Status check failed");
        const data = await res.json();
        setStatus(data.charges_enabled ? "success" : "incomplete");
      } catch {
        setStatus("error");
      }
    };

    checkStatus();
  }, [auth.isAuthenticated, auth.user]);

  const handleRetry = async () => {
    const params = new URLSearchParams(window.location.search);
    const companyId = params.get("company_id");
    setStatus("checking");
    try {
      const res = await fetch(`${API_URL}/stripe/connect/onboard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ company_id: companyId, origin: window.location.origin }),
      });
      if (!res.ok) throw new Error("Could not resume onboarding");
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">

        {status === "checking" && (
          <>
            <svg className="animate-spin h-10 w-10 text-green-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-gray-600 font-medium">Verifying your Stripe account…</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-800 font-semibold text-lg mb-1">Stripe connected!</p>
            <p className="text-gray-500 text-sm mb-5">Your account is ready to accept invoice payments.</p>
            <button
              onClick={() => { window.location.href = "/CompanySettings"; }}
              className="bg-green-500 hover:bg-green-600 text-white font-medium px-5 py-2 rounded-lg text-sm transition"
            >
              Back to Company Settings
            </button>
          </>
        )}

        {status === "incomplete" && (
          <>
            <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-yellow-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <p className="text-gray-800 font-semibold text-lg mb-1">Onboarding incomplete</p>
            <p className="text-gray-500 text-sm mb-5">Your Stripe account needs a few more details before it can accept payments.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleRetry}
                className="bg-green-500 hover:bg-green-600 text-white font-medium px-5 py-2 rounded-lg text-sm transition"
              >
                Continue setup
              </button>
              <button
                onClick={() => { window.location.href = "/CompanySettings"; }}
                className="border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium px-5 py-2 rounded-lg text-sm transition"
              >
                Do it later
              </button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-gray-800 font-semibold text-lg mb-1">Something went wrong</p>
            <p className="text-gray-500 text-sm mb-5">We couldn't verify your Stripe account. Please try again.</p>
            <button
              onClick={() => { window.location.href = "/CompanySettings"; }}
              className="bg-green-500 hover:bg-green-600 text-white font-medium px-5 py-2 rounded-lg text-sm transition"
            >
              Back to Company Settings
            </button>
          </>
        )}

      </div>
    </div>
  );
}
